import { resources } from "@constants/hardcoded-strings";
import { initialIdentityProviderDetails } from "@constants/initial-identity-provider-detail";
import { IdentityProviderDetailsTabContainer } from "@containers/identity-provider-details/identity-provider-details-tab-container/identity-provider-details-details-tab-container";
import { ButtonTypes } from "@enums/button-types";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { formatIdentityProviderDetails } from "@formatters/identity-providers";
import { IdentityProviderValidationErrors } from "@idw-types/common/validation/identity-provider/identity-provider-validation-errors";
import {
    FormattedIdentityProviderDetails
} from "@idw-types/data/identity-providers";
import { IdentityProviderDetailsReferenceData } from "@idw-types/state/identity-provider-details-state";
import { updateIdentityProviderReduxRequest } from "@redux-requests/identity-provider-details";
import { deployIdentityProviderChangeRequestReduxRequest } from "@redux-requests/identity-providers/deploy-identity-provider-request";
import {
    createIdentityProviderRequest, getIdentityProviderDetailsAxnAdminRequest
} from "@requests/identity-provider";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { getIdentityProviderValidationErrors } from "@utils/validation/identity-provider/identity-provider-validation-util";
import { useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import { ButtonWithSpinner } from "../../common/button-with-spinner/button-with-spinner";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import defaultStyles from "../../common/modals/default-modal/default-modal.less";
import { AxnIdentityProvider } from "@idw-types/data/reference-data/axn";

type AddIdentityProviderModalProps = {
    onModalClose: () => void;
    onIdpCreate?: (identityProvider: AxnIdentityProvider) => void;
    identityProviderReferenceData: IdentityProviderDetailsReferenceData;
    organizationId: string;
};

type AddIdentityProviderModalState = {
    requestInProgress: boolean;
};

export const AddIdentityProviderOrgModal = (props: AddIdentityProviderModalProps) => {
    const dispatch = useDispatch();

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);

    const { onModalClose, onIdpCreate, identityProviderReferenceData, organizationId } = props;
    const [state, setState] = useState<AddIdentityProviderModalState>({
        requestInProgress: false
    });
    const { classifications, loas, categories, schemes, companies, protocols } = identityProviderReferenceData;

    const [identityProviderDetailsState, setIdentityProviderDetailsState] = useState<FormattedIdentityProviderDetails>
        (formatIdentityProviderDetails(
            initialIdentityProviderDetails(organizationId),
            categories,
            classifications,
            companies,
            loas,
            protocols,
            schemes
        ));

    const [currentChangeRequestStatus, setCurrentChangeRequestStatus] = useState(ChangeRequestStatusTypes.UNSUBMITTED);
    const [hasDeploymentError, setHasDeploymentError] = useState(false);

    const getIdentityProviderDetails = async (identityProviderId: string): Promise<FormattedIdentityProviderDetails> => {
        const { data: identityProviderDetails, error } = await getIdentityProviderDetailsAxnAdminRequest(identityProviderId);

        if (identityProviderDetails) {
            const formattedIdentityProviderConfigurations = formatIdentityProviderDetails(
                identityProviderDetails,
                categories,
                classifications,
                companies,
                loas,
                protocols,
                schemes
            );

            const mergedIdentityproviderDetails = {
                ...identityProviderDetailsState,
                id: identityProviderId,
                companyIDPInfo: {
                    ...identityProviderDetailsState.companyIDPInfo,
                    id: formattedIdentityProviderConfigurations.companyIDPInfo.id
                }
            }

            setIdentityProviderDetailsState(mergedIdentityproviderDetails);

            return mergedIdentityproviderDetails;
        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.getIdentityProviderDetailsAxn.error.title,
                resources.requests.getIdentityProviderDetailsAxn.error.message
            );
        }

        return null;
    }

    const handleUseBlankSubmit = async (): Promise<FormattedIdentityProviderDetails> => {
        const { data, error } = await createIdentityProviderRequest(undefined, undefined);
        if (data?.response) {
            displaySuccessModal(
                dispatch,
                resources.requests.createIdentityProvider.success.title,
                resources.requests.createIdentityProvider.success.message
            );

            return await getIdentityProviderDetails(data.response.identityProviderID);


        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.createIdentityProvider.error.title,
                resources.requests.createIdentityProvider.error.message
            );
        }

        return null;
    };

    const handleSaveDeployAndClose = async (): Promise<void> => {
        setState({ ...state, requestInProgress: true });
        const idpDetailsResponse = await handleUseBlankSubmit();

        if (idpDetailsResponse) {
            const response = await updateIdentityProviderReduxRequest(dispatch)(
                idpDetailsResponse,
                categories,
                classifications,
                companies,
                loas,
                protocols,
                schemes
            );

            if (response) {
                // Deploy the IDP 
                setCurrentChangeRequestStatus(ChangeRequestStatusTypes.UNSUBMITTED);
                await deployIdentityProviderDetails(response, ChangeRequestStatusTypes.UNSUBMITTED);
            }
        }

        setState({ ...state, requestInProgress: false });
    }

    const deployIdentityProviderDetails = async (identityProvider: FormattedIdentityProviderDetails, currentChangeRequestStatus: ChangeRequestStatusTypes) => {
        const { name, category, classification, code, loa, id, company } = identityProvider;
        
        const updatedChangeRequestStatus = await deployIdentityProviderChangeRequestReduxRequest(dispatch)(id, currentChangeRequestStatus);
        if (updatedChangeRequestStatus === ChangeRequestStatusTypes.DEPLOYED) {
            setCurrentChangeRequestStatus(updatedChangeRequestStatus);
            onIdpCreate({
                name: name,
                category: category.value,
                classificationID: classification.id,
                code: code,
                companyID: company.id,
                loa: loa.value,
                id: id
            });
            onModalClose();
        } else {
            setHasDeploymentError(true);
        }
    }

    const handleDployIdententityProvider = async (): Promise<void> => {
        setState({ ...state, requestInProgress: true });
        await deployIdentityProviderDetails(identityProviderDetailsState, currentChangeRequestStatus);
        setState({ ...state, requestInProgress: false });
    }

    const validationErrors = useMemo((): IdentityProviderValidationErrors => {
        if (identityProviderDetailsState) {
            return getIdentityProviderValidationErrors(identityProviderDetailsState);
        }

        return {
            count: 0,
            generalInfo: {
                tabTitle: null,
                count: 0,
                nameError: null,
                codeError: null,
                categoryError: null,
                organizationError: null
            },
            companyIDP: {
                tabTitle: null,
                count: 0,
                scopeError: null,
                clientIdError: null,
                clientSecretError: null,
                authorizationURLError: null,
                tokenURLError: null,
                userInfoURLError: null
            },
            customURLParameters: {
                tabTitle: null,
                count: 0,
                customURLParametersErrors: {}
            },
            federatedAttributes: {
                tabTitle: null,
                count: 0,
                federatedAttributesErrors: {}
            }
        }

    }, [identityProviderDetailsState]);

    return (
        <DefaultModal
            automationId="add-identity-provider-org-modal"
            ariaLabelledBy="add-identity-provider-org-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={true}
        >
            <Form>
                <Modal.Header id={"add-identity-provider-org-title"} closeButton>
                    <Modal.Title>{resources.modals.addIdentityProvider.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <span className={defaultStyles.description}>
                        {resources.modals.addIdentityProvider.descriptionForOrgDetails}
                    </span>

                    <IdentityProviderDetailsTabContainer
                        labelWidth={labelWidth}
                        isDisabled={state.requestInProgress}
                        identityProviderDetailsState={identityProviderDetailsState}
                        identityProviderReferenceData={identityProviderReferenceData}
                        validationErrors={validationErrors}
                        setIdentityProviderDetailsState={setIdentityProviderDetailsState} />

                </Modal.Body>
                <Modal.Footer>
                    <Button
                        id="add-identity-provider-org-cancel-button"
                        variant="secondary"
                        onClick={onModalClose}>
                        {resources.common.buttons.cancel.label}
                    </Button>

                    <ButtonWithSpinner
                        id="add-identity-provider-org-update-and-close-button"
                        disabled={state.requestInProgress || validationErrors.count > 0}
                        hideSpinner={validationErrors.count > 0}
                        variant="primary"
                        type={ButtonTypes.BUTTON}
                        label={"Update and Close"}
                        onClick={!hasDeploymentError ? handleSaveDeployAndClose : handleDployIdententityProvider}
                    />
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};